// src/roles/store/ProcurementQueue.js
// v2 — D-304 B (Stabilization P2): the queue is calculated LIVE.
//
// WHY THIS CHANGED
// v1 decided what to show from flags written once and never cleared
// (purchase_status "batched", item_status "approved_pending_issue"), looked
// items up by code / name, and wrote PR + MRF marks from a stale page
// snapshot. Lines vanished forever (cancelled PR, short-closed PO, stock
// used elsewhere), batching 2 lines of one MRF marked only the last, and
// two tabs could order the same need twice.
//
// NOW, per item (src/utils/procurementNeed.js, Jest-tested):
//     To buy = Need − In store − Already ordered
// - Need: every open MRF line, whatever its old flags say
// - In store: live central stock
// - Already ordered: open PRs (no PO yet) + open store POs not received
// Items are matched by item_id only. Nothing is skipped silently — lines a
// person must fix first are listed under "Needs attention".
// Creating PRs goes through purchaseRequestService.createPurchaseRequests()
// — one transaction that refuses if any MRF changed since this page loaded.

import React, { useEffect, useMemo, useState } from "react";

import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { useNavigate } from "react-router-dom";

import { db } from "../../firebase";

import {
  ATTENTION_REASONS,
  buildMasterIndex,
  stockByItem,
  openOrdersByItem,
  collectNeedLines,
  buildProcurementRows,
  allocateSources,
  uncoveredLines,
} from "../../utils/procurementNeed";

import { createPurchaseRequests } from "../../services/purchaseRequestService";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

const STATUS_BADGE = {
  to_buy:   { label: "To buy",                  cls: "bg-red-100 text-red-700" },
  ordered:  { label: "Ordered — wait for GRN",  cls: "bg-violet-100 text-violet-700" },
  in_store: { label: "In store — issue it",     cls: "bg-emerald-100 text-emerald-700" },
};

export default function ProcurementQueue() {

  const navigate = useNavigate();

  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = userData.company_id || "";

  const [loading,     setLoading]     = useState(true);
  const [loadError,   setLoadError]   = useState("");
  const [rows,        setRows]        = useState([]);
  const [attention,   setAttention]   = useState([]);
  const [mrfVersions, setMrfVersions] = useState({});
  const [vendorsList, setVendorsList] = useState([]);

  // per item_id
  const [selected,  setSelected]  = useState({});
  const [orderQty,  setOrderQty]  = useState({});
  const [vendorFor, setVendorFor] = useState({});

  const [generating, setGenerating] = useState(false);

  // ============================================
  // LOAD — 6 company-scoped queries, everything else in memory
  // ============================================

  const fetchQueue = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [mrfSnap, itemsSnap, invSnap, vendorsSnap, poSnap, prSnap] = await Promise.all([
        getDocs(query(
          collection(db, "material_requests"),
          where("company_id", "==", companyId),
          where("status", "in", ["approved", "partially_issued"])
        )),
        getDocs(query(
          collection(db, "items"),
          where("company_id", "in", [companyId, ""])
        )),
        getDocs(query(
          collection(db, "inventory_summary"),
          where("company_id", "==", companyId)
        )),
        getDocs(query(
          collection(db, "vendors"),
          where("company_id", "==", companyId)
        )),
        getDocs(query(
          collection(db, "purchase_orders"),
          where("company_id", "==", companyId),
          where("grn_locked", "==", false)
        )),
        getDocs(query(
          collection(db, "purchase_requests"),
          where("company_id", "==", companyId)
        )),
      ]);

      const toPairs = (snap) => snap.docs.map((d) => ({ id: d.id, data: d.data() }));

      const masterIndex = buildMasterIndex(itemsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      const mrfDocs     = toPairs(mrfSnap);

      const { lines, attention: att } = collectNeedLines(mrfDocs, masterIndex);
      const builtRows = buildProcurementRows({
        needLines:  lines,
        stock:      stockByItem(toPairs(invSnap)),
        openOrders: openOrdersByItem({
          purchaseOrders:   toPairs(poSnap),
          purchaseRequests: toPairs(prSnap),
        }),
      });

      // updated_at of every MRF as loaded — the save refuses if any of them
      // changed in the meantime (another user ordered / issued).
      const versions = {};
      for (const m of mrfDocs) {
        const ts = m.data.updated_at;
        versions[m.id] = ts && typeof ts.toMillis === "function" ? ts.toMillis() : null;
      }

      const vendors = vendorsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Defaults: order qty = To buy; vendor = Item Master preferred vendor.
      const qty = {};
      const ven = {};
      for (const r of builtRows) {
        if (r.status !== "to_buy") continue;
        qty[r.item_id] = String(r.to_buy);
        if (r.preferred_vendor_id && vendors.some((v) => v.id === r.preferred_vendor_id)) {
          ven[r.item_id] = { vendor_id: r.preferred_vendor_id, vendor_name: r.preferred_vendor_name || "" };
        }
      }

      setRows(builtRows);
      setAttention(att);
      setMrfVersions(versions);
      setVendorsList(vendors);
      setOrderQty(qty);
      setVendorFor(ven);
      setSelected({});

    } catch (error) {
      console.error("PROCUREMENT QUEUE ERROR:", error);
      setLoadError(error.message || "Could not load the procurement queue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueue();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buyRows = useMemo(() => rows.filter((r) => r.status === "to_buy"), [rows]);
  const counts = useMemo(() => ({
    to_buy:   buyRows.length,
    ordered:  rows.filter((r) => r.status === "ordered").length,
    in_store: rows.filter((r) => r.status === "in_store").length,
  }), [rows, buyRows]);

  const allSelected = buyRows.length > 0 && buyRows.every((r) => selected[r.item_id]);

  const toggleSelect = (itemId) =>
    setSelected((prev) => ({ ...prev, [itemId]: !prev[itemId] }));

  const toggleSelectAll = () => {
    if (allSelected) { setSelected({}); return; }
    const next = {};
    buyRows.forEach((r) => { next[r.item_id] = true; });
    setSelected(next);
  };

  // ============================================
  // CREATE PURCHASE REQUESTS — one per vendor, one transaction
  // ============================================

  const generatePurchaseRequests = async () => {
    if (generating) return;

    const chosen = buyRows.filter((r) => selected[r.item_id]);
    if (chosen.length === 0) { alert("Select items first"); return; }

    const problems = [];
    for (const r of chosen) {
      if (!vendorFor[r.item_id]?.vendor_id) problems.push(`• ${r.item_name}: vendor not selected`);
      if (!(Number(orderQty[r.item_id]) > 0)) problems.push(`• ${r.item_name}: quantity must be more than 0`);
    }
    if (problems.length > 0) {
      alert(`Please fix before creating the purchase request:\n\n${problems.join("\n")}`);
      return;
    }

    const groupsByVendor = {};
    for (const r of chosen) {
      const v = vendorFor[r.item_id];
      const qty = Number(orderQty[r.item_id]);
      if (!groupsByVendor[v.vendor_id]) {
        groupsByVendor[v.vendor_id] = { vendor_id: v.vendor_id, vendor_name: v.vendor_name, rows: [] };
      }
      groupsByVendor[v.vendor_id].rows.push({
        item_id:       r.item_id,
        item_code:     r.item_code,
        item_name:     r.item_name,
        specification: r.specification,
        unit:          r.unit,
        category:      r.category,
        qty,
        // The MRF lines this order is for — the ones not yet covered by
        // stock or earlier orders, oldest first. Extra qty is stock top-up.
        sources:       allocateSources(uncoveredLines(r), qty),
      });
    }

    const touched = new Set(
      Object.values(groupsByVendor).flatMap((g) => g.rows.flatMap((r) => r.sources.map((s) => s.mrf_id)))
    );
    const versions = {};
    touched.forEach((id) => { versions[id] = mrfVersions[id] ?? null; });

    setGenerating(true);
    try {
      const created = await createPurchaseRequests({
        company_id:      companyId,
        groups:          Object.values(groupsByVendor),
        mrfVersions:     versions,
        created_by:      userData.uid || userData.id || "",
        created_by_name: userData.name || userData.email || "",
      });

      alert(
        `Purchase request${created.length > 1 ? "s" : ""} created:\n\n` +
        created.map((c) => `• ${c.purchase_request_no} — ${c.vendor_name}`).join("\n")
      );

      navigate("/purchase-list", { state: { purchase_request_id: created[0]?.id } });

    } catch (error) {
      console.error("PURCHASE REQUEST ERROR:", error);
      alert(error.message || "Could not create the purchase request. Nothing was saved.");
      if (/changed|reload|no longer open|deleted/i.test(error.message || "")) {
        await fetchQueue();
      }
    } finally {
      setGenerating(false);
    }
  };

  // ============================================
  // UI
  // ============================================

  return (

  <div className="p-5 pb-24 bg-gradient-to-br from-slate-100 via-gray-100 to-slate-200 min-h-screen">

    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800">Procurement Queue</h1>
        <p className="text-sm text-gray-500 mt-1">
          To buy = Need (open MRFs) − In store − Already ordered. Worked out fresh every time this page opens.
        </p>
      </div>

      <div className="flex gap-3 flex-wrap">
        {[
          { l: "To buy",          v: counts.to_buy,    c: "text-red-600" },
          { l: "Ordered",         v: counts.ordered,   c: "text-violet-600" },
          { l: "In store",        v: counts.in_store,  c: "text-emerald-600" },
          { l: "Needs attention", v: attention.length, c: "text-amber-600" },
        ].map((x) => (
          <div key={x.l} className="bg-white shadow-sm border border-gray-200 rounded-2xl px-4 py-2 min-w-[110px]">
            <div className="text-[11px] text-gray-500 uppercase">{x.l}</div>
            <div className={`text-2xl font-bold ${x.c}`}>{x.v}</div>
          </div>
        ))}
      </div>
    </div>

    <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-3 mb-4">
      <button
        onClick={() => navigate("/purchase-list")}
        className="px-4 py-3 rounded-2xl bg-violet-100 hover:bg-violet-200 text-violet-700 font-semibold transition text-sm"
      >
        📋 Purchase Requests
      </button>
      <button
        onClick={() => navigate("/purchase-orders")}
        className="px-4 py-3 rounded-2xl bg-violet-100 hover:bg-violet-200 text-violet-700 font-semibold transition text-sm"
      >
        🛒 Purchase Orders
      </button>
      <button
        onClick={toggleSelectAll}
        disabled={buyRows.length === 0}
        className="px-4 py-3 rounded-2xl bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold transition disabled:opacity-50"
      >
        {allSelected ? `Deselect all (${buyRows.length})` : `Select all to buy (${buyRows.length})`}
      </button>
      <button
        disabled={generating || loading}
        onClick={generatePurchaseRequests}
        className="px-6 py-3 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold shadow-lg disabled:opacity-50"
      >
        {generating ? "Creating..." : "Create Purchase Request"}
      </button>
    </div>

    {loadError && (
      <div className="mb-4 p-4 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">
        {loadError}
      </div>
    )}

    <div className="overflow-x-auto bg-white rounded-2xl border border-gray-200 shadow-sm">
      <table className="w-full min-w-[980px] text-sm">
        <thead className="bg-slate-900 text-white text-xs">
          <tr>
            <th className="text-left px-3 py-3">Select</th>
            <th className="text-left px-3 py-3">Item</th>
            <th className="text-right px-3 py-3">Need</th>
            <th className="text-right px-3 py-3">In store</th>
            <th className="text-left px-3 py-3">Ordered</th>
            <th className="text-right px-3 py-3">To buy</th>
            <th className="text-left px-3 py-3">Vendor *</th>
            <th className="text-left px-3 py-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan="8" className="text-center py-12 text-gray-500">Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan="8" className="text-center py-14 text-gray-500">
                <div className="text-lg font-semibold">Nothing to buy</div>
                <div className="text-sm text-gray-400">Every open MRF line is covered by stock or an open order.</div>
              </td>
            </tr>
          ) : rows.map((r) => {
            const canBuy = r.status === "to_buy";
            const badge  = STATUS_BADGE[r.status];
            return (
              <tr key={r.item_id} className={`border-b border-gray-100 align-top ${canBuy ? "hover:bg-cyan-50/40" : "bg-slate-50/60"}`}>
                <td className="px-3 py-3">
                  {canBuy ? (
                    <input
                      type="checkbox"
                      checked={!!selected[r.item_id]}
                      onChange={() => toggleSelect(r.item_id)}
                      className="w-5 h-5 accent-cyan-600"
                    />
                  ) : (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-slate-200 text-slate-500 font-bold uppercase">—</span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <div className="font-medium text-slate-800">{r.item_name}</div>
                  <div className="text-xs text-slate-500">
                    {r.item_code || r.item_id}
                    {r.specification ? ` · ${r.specification}` : ""}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {r.mrf_count} MRF{r.mrf_count > 1 ? "s" : ""}
                    {r.sites.length > 0 ? ` · ${r.sites.join(", ")}` : ""}
                  </div>
                </td>
                <td className="px-3 py-3 text-right font-semibold text-slate-700">{fmt(r.need)} <span className="text-xs text-slate-400">{r.unit}</span></td>
                <td className="px-3 py-3 text-right text-slate-700">{fmt(r.in_store)}</td>
                <td className="px-3 py-3">
                  {r.ordered > 0 ? (
                    <div className="flex flex-col gap-0.5">
                      <span className="font-semibold text-violet-700">{fmt(r.ordered)}</span>
                      {r.ordered_refs.map((ref) => (
                        <button
                          key={`${ref.type}_${ref.id}`}
                          onClick={() => navigate(ref.type === "PO" ? `/purchase-orders/${ref.id}` : "/purchase-list")}
                          className="text-[11px] text-left text-violet-600 hover:underline"
                        >
                          {ref.no} ({fmt(ref.qty)})
                        </button>
                      ))}
                    </div>
                  ) : <span className="text-slate-400">0</span>}
                </td>
                <td className="px-3 py-3 text-right">
                  {canBuy ? (
                    <div className="flex flex-col items-end gap-1">
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={orderQty[r.item_id] ?? ""}
                        onChange={(e) => setOrderQty((prev) => ({ ...prev, [r.item_id]: e.target.value }))}
                        className="w-24 border border-slate-300 rounded-lg px-2 py-1 text-right font-bold text-red-600"
                      />
                      {Number(orderQty[r.item_id]) !== r.to_buy && (
                        <span className="text-[10px] text-slate-400">needed: {fmt(r.to_buy)}</span>
                      )}
                    </div>
                  ) : <span className="text-slate-400">0</span>}
                </td>
                <td className="px-3 py-3">
                  {canBuy ? (
                    <select
                      value={vendorFor[r.item_id]?.vendor_id || ""}
                      onChange={(e) => {
                        const v = vendorsList.find((x) => x.id === e.target.value);
                        setVendorFor((prev) => ({
                          ...prev,
                          [r.item_id]: v
                            ? { vendor_id: v.id, vendor_name: v.vendor_name || v.name || "" }
                            : { vendor_id: "", vendor_name: "" },
                        }));
                      }}
                      className={`border rounded-lg px-2 py-1 text-sm w-44 ${
                        selected[r.item_id] && !vendorFor[r.item_id]?.vendor_id ? "border-red-400 bg-red-50" : "border-slate-200"
                      }`}
                    >
                      <option value="">— Select Vendor —</option>
                      {vendorsList.map((v) => (
                        <option key={v.id} value={v.id}>{v.vendor_name || v.name || v.id}</option>
                      ))}
                    </select>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-3">
                  <span className={`px-2 py-1 rounded-full text-[11px] font-bold ${badge.cls}`}>{badge.label}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>

    {/* NEEDS ATTENTION — never skipped silently */}
    {!loading && attention.length > 0 && (
      <div className="mt-6 bg-white rounded-2xl border border-amber-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-amber-50 border-b border-amber-200">
          <div className="font-semibold text-amber-800">Needs attention ({attention.length})</div>
          <div className="text-xs text-amber-700">These MRF lines cannot be ordered until the item is fixed.</div>
        </div>
        <table className="w-full text-sm">
          <tbody>
            {attention.map((a, i) => (
              <tr key={`${a.mrf_id}_${a.item_index}_${i}`} className="border-b border-gray-100">
                <td className="px-4 py-2">
                  <div className="font-medium text-slate-800">{a.item_name || a.item_id || "Unnamed item"}</div>
                  <div className="text-[11px] text-slate-400">
                    {a.mrf_no || a.mrf_id}{a.site_name ? ` · ${a.site_name}` : ""}
                  </div>
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">{fmt(a.need)} {a.unit}</td>
                <td className="px-4 py-2 text-amber-800">
                  {ATTENTION_REASONS[a.reason] || a.reason}
                  {a.category ? ` ("${a.category}")` : ""}
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => navigate(`/store-mrf/${a.mrf_id}`)} className="text-xs px-3 py-1 rounded-lg bg-amber-600 text-white mr-2">Open MRF →</button>
                  {a.reason === "tool_or_asset" && (
                    <button onClick={() => navigate("/assets")} className="text-xs px-3 py-1 rounded-lg bg-violet-600 text-white">Asset Registry →</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

  </div>
  );
}